import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import useMyFetch from "@stores/useMyFetch";
import { getClientId } from "@utils/clientId";
import { downloadBlob } from "@utils/toolBox";
import { captureEvent } from "@/lib/analytics";
import { useSurveyStore } from "@stores/survey/survey";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import type { ExportFileFormat, ExportType, SurveyExport, SurveyExportPayload } from "@/types";

export const TABLES_FORMAT: ExportFileFormat = "xlsx_tables";
export const AI_CHAT_FORMAT: ExportFileFormat = "xlsx_ai_chat";
export const REWARDS_FORMAT: ExportFileFormat = "zip_rewards";
export const INDIVIDUAL_FORMATS: ExportFileFormat[] = ["csv", "xlsx", "zip", "sav"];
export const EXPORTS_PAGE_SIZE = 20;

const SORT_COLUMNS: Record<string, string> = { type: "file_format" };

const FORMAT_BY_TYPE: Partial<Record<ExportType, ExportFileFormat>> = {
  tables: TABLES_FORMAT,
  ai_chat: AI_CHAT_FORMAT,
  rewards: REWARDS_FORMAT,
};

export const INDIVIDUAL_FORMAT_LABELS: Record<string, string> = {
  csv: "CSV",
  xlsx: "Excel",
  zip: "Triple S",
  sav: "SPSS",
};

export const exportTypeOf = (fileFormat: ExportFileFormat): ExportType => {
  if (fileFormat === TABLES_FORMAT) return "tables";
  if (fileFormat === AI_CHAT_FORMAT) return "ai_chat";
  if (fileFormat === REWARDS_FORMAT) return "rewards";
  return "individual";
};

export const exportTypeLabel = (fileFormat: ExportFileFormat): string => {
  const type = exportTypeOf(fileFormat);
  if (type === "tables") return "Tables & Crosstabs";
  if (type === "ai_chat") return "AI conversations";
  if (type === "rewards") return "Reward payouts";
  return `Individual responses · ${INDIVIDUAL_FORMAT_LABELS[fileFormat] ?? fileFormat}`;
};

export const defaultExportPayload = (): SurveyExportPayload => ({
  name: "",
  file_format: "xlsx",
  admin: false,
  mode: "expand",
  display: "title",
  locale: null,
  verbatim_format: "translated",
  weighted_data: false,
  population: null,
  codification: true,
  include_emails: false,
  include_skips: false,
  question_names: "",
  survey_customer_ids: "",
  user_attributes: "",
  ai_chat_translation: "raw",
  ai_chat_interview_status: ["valid"],
  crosstab_questions: [],
  crosstab_groups: [],
  pairwise_comparisons: false,
  significance_level: 0.95,
  custom_bases: {},
  target_token: "all",
});

export const payloadFrom = (surveyExport: SurveyExport): SurveyExportPayload => {
  const { token: _token, has_document: _doc, generated_at: _gen, created_at: _c, updated_at: _u, ...payload } = surveyExport;
  return {
    ...defaultExportPayload(),
    ...payload,
    ai_chat_interview_status: payload.ai_chat_interview_status?.length ? payload.ai_chat_interview_status : ["valid"],
    custom_bases: { ...(payload.custom_bases ?? {}) },
  };
};

// "type" is a UI notion: tables / ai_chat map to one Excel format each, individual keeps the picked format.
export interface ExportTypeFlags {
  fileFormat: ExportFileFormat;
  isIndividual: boolean;
  isTables: boolean;
  isAiChat: boolean;
  isRewards: boolean;
  isSpreadsheet: boolean;
  isExcel: boolean;
}

export const exportTypeFlags = (exportType: ExportType, individualFormat: ExportFileFormat): ExportTypeFlags => {
  const isIndividual = exportType === "individual";
  return {
    fileFormat: FORMAT_BY_TYPE[exportType] ?? individualFormat,
    isIndividual,
    isTables: exportType === "tables",
    isAiChat: exportType === "ai_chat",
    isRewards: exportType === "rewards",
    isSpreadsheet: isIndividual && ["csv", "xlsx"].includes(individualFormat),
    isExcel: isIndividual && individualFormat === "xlsx",
  };
};

export const hasCrosstabs = (form: SurveyExportPayload): boolean =>
  form.crosstab_questions.length > 0 || form.crosstab_groups.length > 0;

interface SanitizeContext {
  flags: ExportTypeFlags;
  isAdmin: boolean;
  weightsEnabled: boolean;
  fallbackName: string;
}

// Fields the current type/format does not expose go out at their default, whatever the UI did.
export const sanitizeExportPayload = (
  form: SurveyExportPayload,
  { flags, isAdmin, weightsEnabled, fallbackName }: SanitizeContext,
): SurveyExportPayload => {
  const defaults = defaultExportPayload();
  const payload: SurveyExportPayload = {
    ...form,
    file_format: flags.fileFormat,
    name: form.name.trim() || fallbackName,
  };
  const reset = (...keys: (keyof SurveyExportPayload)[]) =>
    keys.forEach((key) => ((payload as Record<string, unknown>)[key] = defaults[key]));

  if (!(isAdmin && (flags.isExcel || flags.isTables))) reset("admin");
  if (!weightsEnabled) reset("weighted_data");
  if (!flags.isTables) reset("crosstab_questions", "crosstab_groups", "include_skips", "custom_bases");
  if (!flags.isTables || !hasCrosstabs(form)) reset("pairwise_comparisons", "significance_level");
  if (!flags.isAiChat) reset("ai_chat_translation", "ai_chat_interview_status");
  if (!flags.isIndividual) reset("question_names", "codification", "include_emails");
  if (!(isAdmin && flags.isExcel)) reset("survey_customer_ids", "user_attributes");
  if (!flags.isExcel) reset("mode");
  if (!flags.isSpreadsheet) reset("display", "verbatim_format");
  return payload;
};

export const useExportsStore = defineStore("surveyExports", () => {
  const { token: surveyToken } = storeToRefs(useSurveyStore());

  const exports = ref<SurveyExport[]>([]);
  const exportsCount = ref(0);
  const currentPage = ref(1);
  const loading = ref(false);
  const searchQuery = ref("");
  const sortColumn = ref("created_at");
  const sortDirection = ref<"asc" | "desc">("desc");

  const baseUrl = computed(() => `/surveys/${surveyToken.value}/exports`);

  const findByToken = (token: string) => exports.value.find((item) => item.token === token);

  function upsert(surveyExport: SurveyExport) {
    const index = exports.value.findIndex((item) => item.token === surveyExport.token);
    if (index === -1) exports.value.unshift(surveyExport);
    else exports.value[index] = surveyExport;
    return surveyExport;
  }

  async function fetchExports(page = currentPage.value) {
    loading.value = true;
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(EXPORTS_PAGE_SIZE),
      sort: SORT_COLUMNS[sortColumn.value] ?? sortColumn.value,
      direction: sortDirection.value,
    });
    if (searchQuery.value.trim()) params.append("search", searchQuery.value.trim());

    const { response, data } = await useMyFetch(`${baseUrl.value}?${params}`)
      .get()
      .json<{ exports: SurveyExport[]; exports_count: number }>();
    if (response.value?.ok) {
      exports.value = data.value?.exports ?? [];
      exportsCount.value = data.value?.exports_count ?? 0;
      currentPage.value = page;
    }
    loading.value = false;
  }

  const { resetSearchQuery } = useDebouncedSearchQuery(searchQuery, () => fetchExports(1));

  function sortBy(column: string) {
    if (sortColumn.value === column) {
      sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
    } else {
      sortColumn.value = column;
      sortDirection.value = "asc";
    }
    return fetchExports(1);
  }

  async function fetchExport(token: string) {
    const { response, data } = await useMyFetch(`${baseUrl.value}/${token}`).get().json<SurveyExport>();
    return response.value?.ok && data.value ? upsert(data.value) : null;
  }

  async function createExport(payload: SurveyExportPayload) {
    const { response, data } = await useMyFetch(baseUrl.value).post({ export: payload }).json<SurveyExport>();
    if (!response.value?.ok || !data.value) throw new Error("Failed to create export");

    captureEvent("results_export_created", { file_format: payload.file_format });

    return upsert(data.value);
  }

  async function updateExport(token: string, payload: SurveyExportPayload) {
    const { response, data } = await useMyFetch(`${baseUrl.value}/${token}`).patch({ export: payload }).json<SurveyExport>();
    if (!response.value?.ok || !data.value) throw new Error("Failed to update export");
    return upsert(data.value);
  }

  async function deleteExport(token: string) {
    const { response } = await useMyFetch(`${baseUrl.value}/${token}`).delete();
    if (!response.value?.ok) throw new Error("Failed to delete export");

    captureEvent("results_export_deleted");

    await fetchExports();
  }

  async function duplicateExport(token: string) {
    const { response } = await useMyFetch(`${baseUrl.value}/${token}/duplicate`).post().json<SurveyExport>();
    if (!response.value?.ok) throw new Error("Failed to duplicate export");

    captureEvent("results_export_duplicated");

    await fetchExports();
  }

  // Tasks launched from this tab. The client_id is shared by every tab of the browser, so each
  // one receives the completion: only the launcher may download (TASK_HANDLERS.export).
  const launchedTaskIds = new Set<string>();

  // Async: the file comes back through the task-progress toaster (TASK_HANDLERS.export).
  async function generateExport(token: string) {
    const { response, data } = await useMyFetch(`${baseUrl.value}/${token}/generate`)
      .post({ client_id: getClientId() })
      .json<{ task_progress_id: string }>();
    if (!response.value?.ok || !data.value) return null;

    launchedTaskIds.add(data.value.task_progress_id);
    captureEvent("results_export_generated", {
      file_format: findByToken(token)?.file_format,
    });
    return data.value.task_progress_id;
  }

  // True once per task, and only in the tab that launched it.
  const claimLaunchedTask = (taskId: string) => launchedTaskIds.delete(taskId);

  async function downloadExport(token: string, surveyTokenOverride?: string) {
    const url = `/surveys/${surveyTokenOverride ?? surveyToken.value}/exports/${token}/download`;
    const { response, data } = await useMyFetch(url).get().blob();
    if (response.value?.ok && data.value) downloadBlob(data.value, response.value, "export");
  }

  function resetFilters() {
    resetSearchQuery();
    sortColumn.value = "created_at";
    sortDirection.value = "desc";
    currentPage.value = 1;
    exports.value = [];
    exportsCount.value = 0;
  }

  return {
    exports,
    exportsCount,
    currentPage,
    loading,
    searchQuery,
    sortColumn,
    sortDirection,
    findByToken,
    fetchExports,
    sortBy,
    fetchExport,
    createExport,
    updateExport,
    deleteExport,
    duplicateExport,
    generateExport,
    claimLaunchedTask,
    downloadExport,
    resetFilters,
  };
});
